import { getConditionDisplayName } from '../config/skinConditionDisplayNames';
import { normalizeConditionName } from './skinConditionNormalization';
import { isSkinConcernName } from './reportConcernsList';
import {
  CLASS_COLORS_MAP,
  getColorByClassId,
  canonicalSkinConditionName,
  isSkinConditionName,
} from './skinResultsUtils';
import { SCORE_KEY_TO_CONCERN } from './detectionNavigation';
import { findBestViewForConcern } from './detectionNavigation';

const CONDITION_SCORE_KEYS = {
  hydration: 'Hydration',
  oxygen: 'Oxygen',
  smoothness: 'Smoothness',
  dullness: 'Dullness',
  uneven_skintone: 'UnevenSkin',
};

const SCORE_CONCERN_TO_KEY = Object.fromEntries(
  Object.entries(SCORE_KEY_TO_CONCERN).map(([k, v]) => [v, k]),
);

function isConcernAllowed(config, concernName) {
  if (!config?.allowedNames) return true;
  const key = normalizeConditionName(concernName);
  return config.allowedNames.has(key);
}

function parseLabelScore(value) {
  if (value == null) return 0;
  if (typeof value === 'number') return Math.round(value);
  if (typeof value === 'object') {
    const raw = value.score ?? value.value ?? value.percentage ?? value.percent ?? 0;
    return Math.round(Number(raw) || 0);
  }
  return Math.round(parseFloat(value) || 0);
}

function buildFromLabelScores(labelScores, segmentation, showConcerns, showConditions) {
  return Object.entries(labelScores)
    .map(([className, score]) => {
      const key = normalizeConditionName(className);
      if (!key) return null;
      const canonical = canonicalSkinConditionName(key) || key;
      if (!showConcerns && isSkinConcernName(canonical)) return null;
      if (!showConditions && !isSkinConcernName(canonical)) return null;
      if (!isConcernAllowed(segmentation, canonical)) return null;
      const numeric = parseLabelScore(score);
      const isCondition = isSkinConditionName(canonical);
      return {
        key: canonical,
        class_name: canonical,
        label: getConditionDisplayName(canonical),
        score: numeric,
        color: CLASS_COLORS_MAP[canonical] || CLASS_COLORS_MAP[key] || 'rgb(140, 80, 200)',
        confidence: 1,
        view: 'front',
        is_condition: isCondition,
        scoreKey: isCondition ? (SCORE_CONCERN_TO_KEY[canonical] || null) : null,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.score - b.score);
}

function buildFromDetections(detectionsByView, conditionScores, segmentation, showConcerns, showConditions) {
  const grouped = {};

  Object.entries(detectionsByView || {}).forEach(([view, list]) => {
    (list || []).forEach((det) => {
      const className = normalizeConditionName(det?.class_name || det?.className || '');
      if (!className || className === 'Unknown' || className === 'Mole') return;
      if (!showConcerns && isSkinConcernName(className)) return;
      if (!showConditions && !isSkinConcernName(className)) return;
      if (!isConcernAllowed(segmentation, className)) return;

      const confidenceRaw = det?.confidence ?? det?.confidence_score ?? 0;
      const confidence = confidenceRaw <= 1 ? confidenceRaw * 100 : confidenceRaw;
      const scoreRaw = det?.score;
      const score = scoreRaw != null ? Number(scoreRaw) : 100;

      if (!grouped[className]) {
        grouped[className] = {
          confidences: [],
          scores: [],
          class_id: det?.class_id ?? det?.classId,
          bestView: view,
          bestConf: confidence,
        };
      }
      grouped[className].confidences.push(confidence);
      grouped[className].scores.push(score);
      if (confidence > grouped[className].bestConf) {
        grouped[className].bestConf = confidence;
        grouped[className].bestView = view;
      }
    });
  });

  const detectionItems = Object.entries(grouped).map(([className, group]) => {
    const scores = group.scores?.length ? group.scores : [100];
    const combinedScore = Math.min(...scores);
    let color = getColorByClassId(group.class_id ?? 0);
    if (!CLASS_COLORS_MAP[className]) {
      color = CLASS_COLORS_MAP[className] || color;
    }
    return {
      key: className,
      class_name: className,
      label: getConditionDisplayName(className),
      score: Math.round(combinedScore),
      color: CLASS_COLORS_MAP[className] || color,
      confidence: Math.round(group.bestConf),
      view: group.bestView || findBestViewForConcern(detectionsByView, className),
      is_condition: false,
      scoreKey: null,
    };
  });

  const conditionItems = [];
  if (showConcerns) {
    Object.entries(CONDITION_SCORE_KEYS).forEach(([scoreKey, displayName]) => {
      if (!isConcernAllowed(segmentation, displayName)) return;
      const raw = conditionScores?.[scoreKey];
      if (raw == null) return;
      const numeric = typeof raw === 'number' ? Math.round(raw) : Math.round(parseFloat(raw) || 0);
      conditionItems.push({
        key: displayName,
        class_name: displayName,
        label: getConditionDisplayName(displayName),
        score: numeric,
        color: CLASS_COLORS_MAP[displayName] || 'rgb(140, 80, 200)',
        confidence: 1,
        view: 'front',
        is_condition: true,
        scoreKey,
      });
    });
  }

  const merged = new Map();
  [...detectionItems, ...conditionItems].forEach((item) => {
    const existing = merged.get(item.key);
    if (!existing || item.score < existing.score) {
      merged.set(item.key, item);
    }
  });

  return Array.from(merged.values()).sort((a, b) => a.score - b.score);
}

/**
 * Unified concern + skin quality list — mirrors SkinRoutinePage concernsList.
 */
export function buildDetailedConcernItems(report, segmentation) {
  if (!report || !segmentation?.loaded) return [];

  const showConcerns = segmentation.showConcerns;
  const showConditions = segmentation.showConditions;
  const labelScores = report?.skin_score?.skin_label_scores;

  if (labelScores && typeof labelScores === 'object' && Object.keys(labelScores).length > 0) {
    return buildFromLabelScores(labelScores, segmentation, showConcerns, showConditions);
  }

  return buildFromDetections(
    report?.detections || {},
    report?.condition_scores || {},
    segmentation,
    showConcerns,
    showConditions,
  );
}
